import React, { useState, useRef } from 'react';
import { COLORS } from '../../../core/constants/colors';
import { UploadProgress, Document } from '../types';

interface FileUploadProps {
  companyId: number;
  onUploadComplete?: (documentId: number) => void;
  onUploadError?: (error: string) => void;
  acceptedTypes?: string[];
  maxFileSize?: number; // in MB
  existingDocuments?: Document[];
  selectedExistingDocumentIds?: number[];
  onToggleExistingDocument?: (documentId: number) => void;
  loadingExistingDocuments?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  companyId,
  onUploadComplete,
  onUploadError,
  acceptedTypes = ['.pdf', '.docx', '.xlsx', '.txt'],
  maxFileSize = 100,
  existingDocuments = [],
  selectedExistingDocumentIds = [],
  onToggleExistingDocument,
  loadingExistingDocuments = false,
}) => {
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
  };

  const validateFile = (file: File): string | null => {
    // Check file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxFileSize) {
      return `File size exceeds ${maxFileSize}MB limit`;
    }

    // Check file type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedTypes.includes(fileExtension)) {
      return `File type ${fileExtension} is not supported`;
    }

    return null;
  };

  const handleFiles = async (files: File[]) => {
    const newUploads: UploadProgress[] = files.map((file) => ({
      file,
      progress: 0,
      status: 'pending',
    }));

    setUploads((prev) => [...prev, ...newUploads]);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const uploadIndex = uploads.length + i;

      // Validate file
      const validationError = validateFile(file);
      if (validationError) {
        updateUploadStatus(uploadIndex, 'failed', 0, validationError);
        if (onUploadError) onUploadError(validationError);
        continue;
      }

      await uploadFile(file, uploadIndex);
    }
  };

  const uploadFile = async (file: File, uploadIndex: number) => {
    try {
      updateUploadStatus(uploadIndex, 'uploading', 0);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'general');

      const token = sessionStorage.getItem('idToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          updateUploadStatus(uploadIndex, 'uploading', percentComplete);
        }
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          updateUploadStatus(uploadIndex, 'processing', 100);

          // Simulate processing status check
          setTimeout(() => {
            updateUploadStatus(uploadIndex, 'completed', 100);
            if (onUploadComplete && response.id) {
              onUploadComplete(response.id);
            }
          }, 2000);
        } else if (xhr.status === 401 || xhr.status === 403) {
          // Token expired or unauthorized
          const error = 'Authentication error. Please try again or re-login.';
          updateUploadStatus(uploadIndex, 'failed', 0, error);
          if (onUploadError) onUploadError(error);
          // DON'T auto redirect - let user handle it
        } else {
          const error = 'Upload failed';
          updateUploadStatus(uploadIndex, 'failed', 0, error);
          if (onUploadError) onUploadError(error);
        }
      });

      xhr.addEventListener('error', () => {
        const error = 'Network error occurred';
        updateUploadStatus(uploadIndex, 'failed', 0, error);
        if (onUploadError) onUploadError(error);
      });

      xhr.open('POST', `${API_BASE_URL}/api/v1/documents/companies/${companyId}/documents/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    } catch (error: any) {
      updateUploadStatus(uploadIndex, 'failed', 0, error.message);
      if (onUploadError) onUploadError(error.message);
    }
  };

  const updateUploadStatus = (
    index: number,
    status: UploadProgress['status'],
    progress: number,
    error?: string
  ) => {
    setUploads((prev) =>
      prev.map((upload, i) =>
        i === index
          ? { ...upload, status, progress, error }
          : upload
      )
    );
  };

  const removeUpload = (index: number) => {
    setUploads((prev) => prev.filter((_, i) => i !== index));
  };

  const getStatusColor = (status: UploadProgress['status']) => {
    switch (status) {
      case 'completed':
        return COLORS.primary.softLime;
      case 'failed':
        return COLORS.extended.redSalsa;
      case 'processing':
        return COLORS.extended.orange;
      default:
        return COLORS.user.primary;
    }
  };

  const getStatusText = (status: UploadProgress['status']) => {
    switch (status) {
      case 'pending':
        return 'Pending...';
      case 'uploading':
        return 'Uploading...';
      case 'processing':
        return 'Processing...';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
    }
  };

  return (
    <div>
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${isDragging ? 'border-opacity-100 scale-105' : 'border-opacity-50'
          }`}
        style={{
          borderColor: isDragging ? COLORS.user.primary : COLORS.primary.persianIndigo,
          backgroundColor: isDragging ? COLORS.user.primary + '10' : COLORS.extended.cultured,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleFileSelect}
          className="hidden"
        />

        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: COLORS.user.primary + '20' }}
        >
          <svg
            className="w-8 h-8"
            style={{ color: COLORS.user.primary }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
        </div>

        <h3 className="text-lg font-bold mb-2" style={{ color: COLORS.primary.persianIndigo }}>
          {isDragging ? 'Drop files here' : 'Upload Documents'}
        </h3>
        <p className="text-sm mb-4" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
          Drag and drop files or click to browse
        </p>
        <p className="text-xs" style={{ color: COLORS.primary.persianIndigo, opacity: 0.5 }}>
          Supported formats: {acceptedTypes.join(', ')} • Max size: {maxFileSize}MB
        </p>
      </div>

      {/* Documents List - Combined existing and new uploads */}
      {(existingDocuments.length > 0 || uploads.length > 0) && (
        <div className="mt-6 space-y-3">
          <h4 className="font-semibold mb-3" style={{ color: COLORS.primary.persianIndigo }}>
            Documents ({existingDocuments.length + uploads.length})
          </h4>

          {loadingExistingDocuments ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: COLORS.user.primary }}></div>
            </div>
          ) : (
            <>
              {/* Existing documents */}
              {existingDocuments.map((doc) => {
                const isSelected = selectedExistingDocumentIds.includes(doc.id);
                return (
                  <div
                    key={`existing-${doc.id}`}
                    onClick={() => onToggleExistingDocument?.(doc.id)}
                    className="bg-white rounded-lg p-4 cursor-pointer transition-all border-2"
                    style={{
                      borderColor: isSelected ? COLORS.user.primary : COLORS.extended.cultured,
                      backgroundColor: isSelected ? COLORS.user.primary + '05' : 'white',
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all"
                        style={{
                          borderColor: isSelected ? COLORS.user.primary : '#D1D5DB',
                          backgroundColor: isSelected ? COLORS.user.primary : 'transparent',
                        }}
                      >
                        {isSelected && (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: COLORS.user.primary + '20' }}
                      >
                        <svg
                          className="w-5 h-5"
                          style={{ color: COLORS.user.primary }}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate" style={{ color: COLORS.primary.persianIndigo }}>
                          {doc.original_file_name || doc.file_name}
                        </p>
                        <p className="text-xs" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
                          {(doc.file_size / (1024 * 1024)).toFixed(2)} MB • {doc.file_type?.toUpperCase() || 'FILE'}
                        </p>
                      </div>
                      <span
                        className="text-xs font-semibold px-2 py-1 rounded"
                        style={{
                          backgroundColor: doc.status === 'completed' ? COLORS.primary.softLime + '20' : COLORS.extended.orange + '20',
                          color: doc.status === 'completed' ? COLORS.primary.softLime : COLORS.extended.orange,
                        }}
                      >
                        {doc.status}
                      </span>
                    </div>
                  </div>
                );
              })}

              {/* New uploads */}
              {uploads.map((upload, index) => (
                <div
                  key={`upload-${index}`}
                  className="bg-white rounded-lg p-4"
                  style={{ border: `1px solid ${COLORS.extended.cultured}` }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: getStatusColor(upload.status) + '20' }}
                    >
                      {upload.status === 'completed' ? (
                        <svg
                          className="w-5 h-5"
                          style={{ color: getStatusColor(upload.status) }}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : upload.status === 'failed' ? (
                        <svg
                          className="w-5 h-5"
                          style={{ color: getStatusColor(upload.status) }}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5 animate-spin"
                          style={{ color: getStatusColor(upload.status) }}
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-semibold text-sm truncate" style={{ color: COLORS.primary.persianIndigo }}>
                          {upload.file.name}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeUpload(index);
                          }}
                          className="text-xs hover:underline ml-2"
                          style={{ color: COLORS.primary.persianIndigo, opacity: 0.5 }}
                        >
                          Remove
                        </button>
                      </div>

                      <p className="text-xs mb-2" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
                        {(upload.file.size / (1024 * 1024)).toFixed(2)} MB • {getStatusText(upload.status)}
                      </p>

                      {upload.error && (
                        <p className="text-xs mb-2" style={{ color: COLORS.extended.redSalsa }}>
                          {upload.error}
                        </p>
                      )}

                      {(upload.status === 'uploading' || upload.status === 'processing') && (
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all"
                            style={{
                              backgroundColor: getStatusColor(upload.status),
                              width: `${upload.progress}%`,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};